import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { OrderUnitOfMeasure } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { ReactNode, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { MaterialsV2Dto } from '../OrderFormV2/components/MaterialsV2';
import { TrucksV2Dto } from '../OrderFormV2/components/TrucksV2';
import { EstimateCard, FlexRow } from './shared-components';

const NO_VALUE_PLACEHOLDER = '–';

const ErrorMessage = ({ children }: { children: ReactNode }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 0.5,
        justifyContent: 'center',
        py: 0.5,
      }}
    >
      <ErrorOutlineIcon
        sx={{ fontSize: '14px', color: theme.brandV2.colors.treadGray2 }}
      />
      <Typography
        sx={{ fontSize: '10px', fontWeight: 600, color: theme.brandV2.colors.treadGray2 }}
      >
        {children}
      </Typography>
    </Box>
  );
};

const DeliveryEstimatesV2 = observer(() => {
  const form = useFormContext<TrucksV2Dto>();
  const materialsForm = useFormContext<MaterialsV2Dto>();

  const watchedMaterialUnitOfMeasure = materialsForm.watch('unitOfMeasure');
  const watchedMaterialQuantity = materialsForm.watch('quantity');
  const watchedTruckCount = form.watch('truckCount');
  const watchedJobQuantity = form.watch('jobQuantity');
  const watchedJobUnitOfMeasure = form.watch('jobUnitOfMeasure');
  const watchedRecurringLoads = Boolean(form.watch('recurringLoads'));
  const watchedEquipmentTypeGrossCapacity = form.watch('equipmentTypeGrossCapacity');
  const watchedEquipmentTypeGrossCapacityUnitOfMeasure = form.watch(
    'equipmentTypeGrossCapacityUnitOfMeasure',
  );
  const watchedCycleTime = form.watch('cycleTime');

  const totalLoads: ReactNode = useMemo(() => {
    if (
      watchedTruckCount &&
      watchedJobUnitOfMeasure?.id === OrderUnitOfMeasure.LOAD &&
      watchedRecurringLoads &&
      watchedJobQuantity
    ) {
      return (
        <Typography sx={{ fontSize: '14px' }}>
          {t('common.running')} ({watchedTruckCount * watchedJobQuantity}+)
        </Typography>
      );
    }

    if (
      watchedRecurringLoads &&
      watchedJobUnitOfMeasure?.id === OrderUnitOfMeasure.LOAD &&
      !watchedJobQuantity
    ) {
      return <Typography sx={{ fontSize: '14px' }}>{t('common.running')}</Typography>;
    }

    if (
      watchedTruckCount &&
      watchedJobUnitOfMeasure?.id === OrderUnitOfMeasure.LOAD &&
      watchedJobQuantity
    ) {
      return (
        <Typography sx={{ fontSize: '14px' }}>
          {watchedTruckCount * watchedJobQuantity}
        </Typography>
      );
    }

    if (
      watchedMaterialQuantity &&
      watchedMaterialUnitOfMeasure?.id ===
        watchedEquipmentTypeGrossCapacityUnitOfMeasure?.id &&
      watchedEquipmentTypeGrossCapacity
    ) {
      const totalLoads = watchedMaterialQuantity / watchedEquipmentTypeGrossCapacity;
      return <Typography sx={{ fontSize: '14px' }}>{Math.ceil(totalLoads)}</Typography>;
    }

    if (
      watchedTruckCount &&
      watchedJobQuantity &&
      watchedEquipmentTypeGrossCapacity &&
      watchedEquipmentTypeGrossCapacityUnitOfMeasure?.id === watchedJobUnitOfMeasure?.id
    ) {
      return (
        <Typography sx={{ fontSize: '14px' }}>
          {Math.ceil(
            (watchedTruckCount * watchedJobQuantity) / watchedEquipmentTypeGrossCapacity,
          )}
        </Typography>
      );
    }

    return null;
  }, [
    watchedTruckCount,
    watchedJobUnitOfMeasure,
    watchedRecurringLoads,
    watchedJobQuantity,
    watchedEquipmentTypeGrossCapacity,
    watchedEquipmentTypeGrossCapacityUnitOfMeasure,
    watchedMaterialQuantity,
    watchedMaterialUnitOfMeasure,
  ]);

  const deliveredQuantity: ReactNode = useMemo(() => {
    if (
      watchedTruckCount &&
      watchedJobQuantity &&
      watchedJobUnitOfMeasure?.id === OrderUnitOfMeasure.LOAD &&
      watchedEquipmentTypeGrossCapacity
    ) {
      return (
        <Typography sx={{ fontSize: '14px' }}>
          {watchedTruckCount * watchedJobQuantity * watchedEquipmentTypeGrossCapacity}{' '}
          {watchedEquipmentTypeGrossCapacityUnitOfMeasure?.name}
        </Typography>
      );
    }

    if (
      watchedJobUnitOfMeasure?.id !== watchedEquipmentTypeGrossCapacityUnitOfMeasure?.id
    ) {
      return (
        <ErrorMessage>
          {t('dispatch.order.delivery_estimates.units_mismatch_message')}
        </ErrorMessage>
      );
    }

    if (watchedTruckCount && watchedJobQuantity && watchedJobUnitOfMeasure) {
      return (
        <Typography sx={{ fontSize: '14px' }}>
          {watchedTruckCount * watchedJobQuantity} {watchedJobUnitOfMeasure?.name}
        </Typography>
      );
    }

    return null;
  }, [
    watchedTruckCount,
    watchedJobQuantity,
    watchedJobUnitOfMeasure,
    watchedEquipmentTypeGrossCapacity,
    watchedEquipmentTypeGrossCapacityUnitOfMeasure,
  ]);

  const deliveredPerTruck: ReactNode = useMemo(() => {
    if (
      watchedJobUnitOfMeasure?.id === OrderUnitOfMeasure.LOAD &&
      watchedRecurringLoads &&
      watchedJobQuantity
    ) {
      return (
        <Typography sx={{ fontSize: '14px' }}>
          {t('common.running')} ({watchedJobQuantity}+)
        </Typography>
      );
    }

    if (
      watchedRecurringLoads &&
      watchedJobUnitOfMeasure?.id === OrderUnitOfMeasure.LOAD &&
      !watchedJobQuantity
    ) {
      return <Typography sx={{ fontSize: '14px' }}>{t('common.running')}</Typography>;
    }

    if (
      watchedEquipmentTypeGrossCapacity &&
      watchedEquipmentTypeGrossCapacityUnitOfMeasure &&
      watchedJobUnitOfMeasure?.id === OrderUnitOfMeasure.LOAD &&
      watchedJobQuantity
    ) {
      return (
        <Typography sx={{ fontSize: '14px' }}>
          {watchedJobQuantity * watchedEquipmentTypeGrossCapacity}{' '}
          {watchedEquipmentTypeGrossCapacityUnitOfMeasure?.name}
        </Typography>
      );
    }

    if (watchedJobQuantity && watchedJobUnitOfMeasure) {
      return (
        <Typography sx={{ fontSize: '14px' }}>
          {watchedJobQuantity} {watchedJobUnitOfMeasure?.name}
        </Typography>
      );
    }

    return null;
  }, [
    watchedEquipmentTypeGrossCapacity,
    watchedEquipmentTypeGrossCapacityUnitOfMeasure,
    watchedJobUnitOfMeasure,
    watchedJobQuantity,
    watchedRecurringLoads,
  ]);

  const unitsPerHour: ReactNode = useMemo(() => {
    if (
      !watchedCycleTime &&
      watchedEquipmentTypeGrossCapacity &&
      watchedTruckCount &&
      watchedEquipmentTypeGrossCapacityUnitOfMeasure
    ) {
      return (
        <ErrorMessage>
          {t('dispatch.order.delivery_estimates.set_cycle_time_message')}
        </ErrorMessage>
      );
    }
    if (
      watchedCycleTime &&
      watchedEquipmentTypeGrossCapacity &&
      watchedTruckCount &&
      watchedEquipmentTypeGrossCapacityUnitOfMeasure
    ) {
      const cycleTimeInHours = watchedCycleTime / 60;
      const unitsPerHour = Math.round(
        (watchedEquipmentTypeGrossCapacity / cycleTimeInHours) * watchedTruckCount,
      );
      return (
        <Typography sx={{ fontSize: '14px' }}>
          {unitsPerHour} {watchedEquipmentTypeGrossCapacityUnitOfMeasure?.name}
        </Typography>
      );
    }

    return null;
  }, [
    watchedCycleTime,
    watchedEquipmentTypeGrossCapacity,
    watchedTruckCount,
    watchedEquipmentTypeGrossCapacityUnitOfMeasure,
  ]);

  return (
    <>
      <FlexRow sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="form-section-title" sx={{ fontSize: '14px' }}>
          {t('dispatch.order.delivery_estimates.title')}
        </Typography>
      </FlexRow>

      <Grid container spacing={1}>
        <Grid size={{ xs: 3 }}>
          <EstimateCard title={t('dispatch.order.delivery_estimates.total_loads')}>
            {totalLoads || NO_VALUE_PLACEHOLDER}
          </EstimateCard>
        </Grid>

        <Grid size={{ xs: 3 }}>
          <EstimateCard
            title={t('dispatch.order.delivery_estimates.estimated_delivered_qty')}
          >
            {deliveredQuantity || NO_VALUE_PLACEHOLDER}
          </EstimateCard>
        </Grid>

        <Grid size={{ xs: 3 }}>
          <EstimateCard
            title={t('dispatch.order.delivery_estimates.delivered_per_truck')}
            variant="light"
          >
            {deliveredPerTruck || NO_VALUE_PLACEHOLDER}
          </EstimateCard>
        </Grid>

        <Grid size={{ xs: 3 }}>
          <EstimateCard
            title={t('dispatch.order.delivery_estimates.units_per_hour')}
            variant="light"
          >
            {unitsPerHour || NO_VALUE_PLACEHOLDER}
          </EstimateCard>
        </Grid>
      </Grid>
    </>
  );
});

export default DeliveryEstimatesV2;
